import { useEffect } from "react";
import { useFormikContext } from "formik";
import { FiChevronDown } from "react-icons/fi";
import { cn } from "utils/helpers";
import { CompanyLogoDisplay } from "newui/features/Company/components/CompanyLogo";
import { ToggleButtonGroup } from "newui/components/ui/ToggleButtonGroup/ToggleButtonGroup";
import type { ComplianceQuestion } from "newui/features/Rfx/Evaluation/types/compliance";
import { SupplierComment } from "./SupplierComment";
import type { ComplianceGridFormValues } from "./compliance-grid.schema";
import {
	boolToString,
	stringToBool,
	COMPLIANCE_OPTIONS,
	EVALUATED_OPTIONS,
	getCompliancePillState,
	COMPLIANCE_PILL_META,
} from "./compliance-grid.helpers";

export interface SupplierMeta {
	company_name: string | null;
	invite_id: number;
	supplier_logo: string | null;
}

interface SupplierComplianceAccordionItemProps {
	isExpanded: boolean;
	locked: boolean;
	questions: ComplianceQuestion[];
	supplier: SupplierMeta;
	supplierIndex: number;
	onToggle: () => void;
}

type SupplierErrors = Array<
	{ comments?: string; is_evaluated?: string; } | undefined
>;

export const SupplierComplianceAccordionItem = ({ supplier, questions, supplierIndex: sIdx, locked, isExpanded, onToggle }: SupplierComplianceAccordionItemProps) => {
	const { values, setFieldValue, errors } =
		useFormikContext<ComplianceGridFormValues>();

	const supplierValue = values.suppliers[sIdx];
	const supplierErrors = (errors.suppliers as SupplierErrors | undefined) ?? [];
	const evalError = supplierErrors[sIdx]?.is_evaluated;
	const commentError = supplierErrors[sIdx]?.comments;

	const pillState = getCompliancePillState(supplierValue);
	const pill = COMPLIANCE_PILL_META[pillState];

	// (1.5) Auto-flag Compliant for Evaluation = Yes once every question is
	// marked Compliant. Only nudges toward "Yes" — never overrides an explicit
	// decision the lead has already made.
	const allCompliant =
		supplierValue.answers.length > 0 &&
		supplierValue.answers.every((a) => a.is_compliant === true);

	useEffect(() => {
		if (!locked && allCompliant && supplierValue.is_evaluated !== true) {
			setFieldValue(`suppliers[${sIdx}].is_evaluated`, true);
		}
	}, [allCompliant, supplierValue.is_evaluated, locked, sIdx, setFieldValue]);

	const hasQuestions = questions.length > 0;

	return (
		<div
			className={cn(
				"rounded-lg border bg-white transition-colors",
				isExpanded ? "border-[#0031DD]/20" : "border-gray-200",
				locked && "opacity-60"
			)}
			data-testid={`supplier-accordion-${sIdx}`}
		>
			<button
				type="button"
				onClick={onToggle}
				className="flex w-full items-center justify-between rounded-lg px-4 py-3 hover:bg-gray-50"
				aria-expanded={isExpanded}
				data-testid={`supplier-accordion-trigger-${sIdx}`}
			>
				<span className="flex items-center gap-3">
					<CompanyLogoDisplay
						size="sm"
						supplierLogoUrl={supplier.supplier_logo}
						supplierName={supplier.company_name}
					/>
					<span className="text-sm font-medium text-gray-900">
						{supplier.company_name ?? `Supplier ${sIdx + 1}`}
					</span>
				</span>
				<span className="flex items-center gap-3">
					<span
						className={cn(
							"inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
							pill.className
						)}
						data-testid={`supplier-status-pill-${sIdx}`}
						data-pill-state={pillState}
					>
						{pill.label}
					</span>
					<FiChevronDown
						size={16}
						className={cn(
							"text-gray-400 transition-transform",
							isExpanded && "rotate-180"
						)}
						aria-hidden="true"
					/>
				</span>
			</button>

			{isExpanded && (
				<div
					className="border-t border-gray-100 px-4 py-4"
					role="region"
					aria-label={`Compliance for ${supplier.company_name ?? `Supplier ${sIdx + 1}`}`}
				>
					<table className="w-full border-collapse text-sm">
						<thead>
							<tr className="text-left text-xs text-gray-500">
								<th className="border-b border-gray-200 py-2 pr-4 font-medium">
									Question
								</th>
								<th className="border-b border-gray-200 px-4 py-2 font-medium">
									Decision
								</th>
								<th className="border-b border-gray-200 pl-4 py-2 font-medium">
									Comments
								</th>
							</tr>
						</thead>
						<tbody>
							{hasQuestions ? (
								questions.map((question, qIdx) => (
									<tr key={question.id} className="align-top">
										<td className="border-b border-gray-50 py-3 pr-4 font-medium text-gray-800">
											{question.question}
										</td>
										<td className="border-b border-gray-50 px-4 py-3">
											<div
												data-testid={`compliance-toggle-s${sIdx}-q${qIdx}`}
											>
												<ToggleButtonGroup
													options={COMPLIANCE_OPTIONS}
													value={boolToString(
														supplierValue.answers[qIdx]?.is_compliant
													)}
													disabled={locked}
													onChange={(s) => {
														setFieldValue(
															`suppliers[${sIdx}].answers[${qIdx}].is_compliant`,
															stringToBool(s)
														);
													}}
												/>
											</div>
										</td>
										{qIdx === 0 && (
											<td
												className="border-b border-gray-50 pl-4 py-3 align-top"
												rowSpan={questions.length}
											>
												<SupplierComment
													name={`suppliers[${sIdx}].comments`}
													value={supplierValue.comments}
													onChange={(html) => {
														setFieldValue(
															`suppliers[${sIdx}].comments`,
															html
														);
													}}
													disabled={locked}
													supplierIndex={sIdx}
												/>
												{commentError && (
													<p
														className="mt-1 text-xs text-red-600"
														data-testid={`comment-error-${sIdx}`}
													>
														{commentError}
													</p>
												)}
											</td>
										)}
									</tr>
								))
							) : (
								<tr className="align-top">
									<td
										className="border-b border-gray-50 py-3 pr-4 text-gray-500"
										colSpan={2}
									>
										No compliance questions configured.
									</td>
									<td className="border-b border-gray-50 pl-4 py-3 align-top">
										<SupplierComment
											name={`suppliers[${sIdx}].comments`}
											value={supplierValue.comments}
											onChange={(html) => {
												setFieldValue(
													`suppliers[${sIdx}].comments`,
													html
												);
											}}
											disabled={locked}
											supplierIndex={sIdx}
										/>
										{commentError && (
											<p
												className="mt-1 text-xs text-red-600"
												data-testid={`comment-error-${sIdx}`}
											>
												{commentError}
											</p>
										)}
									</td>
								</tr>
							)}
						</tbody>
						<tfoot>
							{/* (1.4) Compliant for Evaluation stays at the bottom. */}
							<tr className="bg-blue-50">
								<td className="py-3 pr-4 font-medium text-gray-700">
									Compliant for Evaluation
								</td>
								<td className="px-4 py-3">
									<div data-testid={`evaluated-toggle-s${sIdx}`}>
										<ToggleButtonGroup
											options={EVALUATED_OPTIONS}
											value={boolToString(supplierValue.is_evaluated)}
											disabled={locked}
											onChange={(s) => {
												setFieldValue(
													`suppliers[${sIdx}].is_evaluated`,
													stringToBool(s)
												);
											}}
										/>
									</div>
								</td>
								<td className="pl-4 py-3">
									{evalError && (
										<p
											className="text-xs text-red-600"
											data-testid={`evaluated-error-${sIdx}`}
										>
											{evalError}
										</p>
									)}
								</td>
							</tr>
						</tfoot>
					</table>
				</div>
			)}
		</div>
	);
};
